import React, { useEffect, useState, useRef, useMemo } from "react";
import { Document, Page } from "react-pdf";
import { Box, Grid, IconButton, TextField, Typography } from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import WaterMark from '@uiw/react-watermark';
import UserRepository from '../../../core/repository/UserRepository';
import { useWatermarkProtection } from "../../../hook/useWatermarkProtection";
import DocumentNote from "../Document/DocumentNote";
import { USER_ROLE } from "../../../core/Constant";

const PdfComponent = ({ pdfFile, documentId: propDocumentId, onBack }) => {
  const getUserTypeFromStorage = () => {
    try {
      const storedUserType = localStorage.getItem("userType");
      if (!storedUserType) return "";
      return storedUserType.startsWith('"')
        ? JSON.parse(storedUserType)
        : storedUserType;
    } catch (e) {
      return localStorage.getItem("userType") || "";
    }
  };
  const [userInfo, setUserInfo] = useState({ name: '', contact: '', email: '', role: getUserTypeFromStorage() });
  useEffect(() => {
    const getUser = async () => {
      const user = await UserRepository.getUserDetail();
      const localType = getUserTypeFromStorage();
      setUserInfo({
        name: `${user?.data?.first_name || ""} ${user?.data?.last_name || ""}`.trim(),
        contact: user?.data?.phone_number || '',
        email: user?.data?.email || '',
        role: localType,
      });
    };
    getUser();
  }, []);

  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [inputPageNumber, setInputPageNumber] = useState(1);
  const [newdocument,setDocument] = useState()
  const [baseUrl,setBaseUrl] = useState("")
  const [watermarkKey, setWatermarkKey] = useState(0);

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const calculatedWidth = useMemo(() => {
    if (!containerWidth) return undefined;
    return Math.round(containerWidth * scale);
  }, [containerWidth, scale]);

  useEffect(() => {
    const viewport = containerRef.current;
    if (!viewport) return undefined;

    const resizeObserver = new ResizeObserver(([entry]) => {
        const measuredWidth = Math.floor(entry.contentRect.width);
        if (measuredWidth > 0) {
          setContainerWidth((currentWidth) =>
            currentWidth === measuredWidth ? currentWidth : measuredWidth
          );
      }
    });

    resizeObserver.observe(viewport);

  return () => {
      resizeObserver.disconnect();
  };
  }, []);

  const hasTriggeredRef = useRef(false);
       
  const sendTamperingAlert = async () => {
           try {
             const res = await UserRepository.temperingAlertMail();
           } catch (e) {
             console.error("Tampering alert error:", e);
           }
         };
     
         useWatermarkProtection({
         onTamper: () => {
           if (!hasTriggeredRef.current) {
           hasTriggeredRef.current = true;
           sendTamperingAlert(); 
         }
         setWatermarkKey(prev => prev + 1);
         }
       });       
  
  useEffect(() => {
    const path = pdfFile?.data?.file_path || pdfFile?.file_path;
    const name = pdfFile?.name || "";
    if (path) {
      // Clean query signatures to compare raw file paths
      const currentRawPath = baseUrl.split('?')[0];
      const newRawPath = path.split('?')[0];

      if (currentRawPath !== newRawPath || !baseUrl) {
        setDocument({ name, path });
        setBaseUrl(path);
        setPageNumber(1);
        setInputPageNumber(1);
      }
    }

    const disableRightClick = (event) => {
      if (event.button === 2) {
        event.preventDefault();
      }
    };
    document.addEventListener("contextmenu", disableRightClick);
    return () => {
      document.removeEventListener("contextmenu", disableRightClick);
    };
  }, [pdfFile, baseUrl]);

    // Memoize the file configuration object so react-pdf doesn't treat it as a new reference on every tick
    const fileSource = useMemo(() => {
    if (!baseUrl) return null;
    return { 
      url: baseUrl,
      withCredentials: false 
    };
  }, [baseUrl]);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
  }

  const goToPreviousPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
      setInputPageNumber(pageNumber - 1);
    }
  };

  const goToNextPage = () => {
    if (pageNumber < numPages) {
      setPageNumber(pageNumber + 1);
      setInputPageNumber(pageNumber + 1);
    }
  };

  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.1, 2.0));
  const handleZoomOut = () => setScale((prev) => Math.max(prev - 0.1, 0.5));

  const handlePageNumberChange = (event) => {
    setInputPageNumber(event.target.value);
  };

  const handlePageNumberSubmit = () => {
    const newPageNumber = Number(inputPageNumber);
    if (!isNaN(newPageNumber) && newPageNumber >= 1 && newPageNumber <= numPages) {
      setPageNumber(newPageNumber);
    } else {
      setInputPageNumber(pageNumber);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handlePageNumberSubmit();
    }
  };

  const isStudent =
    String(userInfo.role).toLowerCase() === String(USER_ROLE.student).toLowerCase();

  return (
    <Box sx={{ width: "100%", py: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 3,
          py: 1.5,
          mb: 3,
          backgroundColor: "#FFFFFF",
          borderBottom: "1px solid #F0F0F0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
          }}
        >
          {/* Title */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              fontSize: "18px",
              color: "#1F2937",
              whiteSpace: "nowrap",
            }}
          >
            {newdocument?.name?.replace(/\.[^/.]+$/, "") ||
              "Document Viewer"}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton
            onClick={goToPreviousPage}
            disabled={pageNumber <= 1}
             sx={{
                width: 36,
                height: 36,
              border: "1px solid #D8B4FE",
              color: "#9834F0",
              backgroundColor: "#FFFFFF",
              "&:hover": {
                backgroundColor: "#F3E8FF" },
                "&.Mui-disabled": { borderColor: "#E5E7EB", color: "#D1D5DB" },
            }}
          >
            <NavigateBeforeIcon fontSize="small" />
          </IconButton>
          <Typography
              sx={{
                fontWeight:500,
                fontSize: "14px",
                color: "#6B7280"
                }}
            >
            Page
          </Typography>
          <TextField
            value={inputPageNumber}
            onChange={handlePageNumberChange}
            onBlur={handlePageNumberSubmit}
            onKeyPress={handleKeyPress}
            size="small"
            inputProps={{ min: 1, max: numPages }}
            sx={{
                width: "48px",
                "& .MuiOutlinedInput-root": {
                  height: "36px",
                  borderRadius: "6px",
                  "& fieldset": { borderColor: "#E5E7EB" },
                  "&:hover fieldset": { borderColor: "#9834F0" },
                  "&.Mui-focused fieldset": { borderColor: "#9834F0" },
                },
                "& .MuiInputBase-input": {
                  textAlign: "center",
                  fontWeight: 600,
                  fontSize: "14px",
                  padding: "4px",
              },
            }}
          />
          <Typography sx={{fontWeight:'500', fontSize: "14px", color: "#6B7280" }}>
            of {numPages || "..."}
          </Typography>
          <IconButton
            onClick={goToNextPage}
            disabled={pageNumber >= numPages}
            sx={{
              width: 36,
              height: 36,
              border: "1px solid #D8B4FE",
              color: "#9834F0",
              backgroundColor: "#FFFFFF",
              "&:hover": {
                backgroundColor: "#F3E8FF" },
                "&.Mui-disabled": { borderColor: "#E5E7EB", color: "#D1D5DB" },
            }}
          >
            <NavigateNextIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={handleZoomOut}
            disabled={scale <= 0.5}
            sx={{
              width: 36,
              height: 36,
              ml: 1,
              border: "1px solid #D8B4FE",
              color: "#9834F0",
                backgroundColor: "#FFFFFF",
                "&:hover": { backgroundColor: "#F3E8FF" },
                "&.Mui-disabled": { borderColor: "#E5E7EB", color: "#D1D5DB" },
            }}
            >
              <ZoomOutIcon fontSize="small" />
            </IconButton>

            <IconButton
            onClick={handleZoomIn}
            disabled={scale >= 2.0}
              sx={{
                width: 36,
                height: 36,
                border: "1px solid #D8B4FE",
                color: "#9834F0",
                backgroundColor: "#FFFFFF",
                "&:hover": { backgroundColor: "#F3E8FF" },
                "&.Mui-disabled": { borderColor: "#E5E7EB", color: "#D1D5DB" },
              }}
          >
            <ZoomInIcon fontSize="small" />
          </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Main Content Layout */}
      <Grid container spacing={3} sx={{ px: 3, alignItems: "stretch" }}>
        {/* PDF Viewer: takes full width if user is NOT a student */}
        <Grid item xs={12} md={isStudent ? 8 : 12} lg={isStudent ? 8.5 : 12} sx={{ display: "flex", minWidth: 0 }}>
          <Box
            ref={containerRef}
            sx={{
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              minHeight: { xs: "350px", md: "650px" },
              maxHeight: "auto",
              backgroundColor: "#FAFAFA",
              border: "1px solid #E5E7EB",
              borderRadius: "16px",
              p: { xs: 1.5, sm: 3 },
            display: "flex",
            alignItems: scale <= 1.0 ? "center" : "flex-start",
              justifyContent: scale > 1.0 ? "flex-start" : "center",
              overflowX: "auto",
              overflowY: "auto",
              contain: "inline-size",
              scrollbarGutter: "stable",
              boxSizing: "border-box"
            }}
>
        {fileSource && (
        <Document
          file={fileSource}
          onLoadSuccess={onDocumentLoadSuccess}
          style={{ margin: scale > 1.0 ? "0" : "auto" }}
        >
            <Box
                  sx={{
                    display: "flex",
                    justifyContent: scale > 1.0 ? "flex-start" : "center",
                    alignItems: "center",
                    margin: scale > 1.0 ? "0" : "auto",
                    width: scale > 1.0 ? "max-content" : "100%",
                    minWidth: "100%",
                  }}
                >
          <WaterMark  key={watermarkKey} className="watermark-protection"
           content={[
                  userInfo.name,
                  userInfo.contact,
                  userInfo.email,
                ]}
            gap={[120, 120]}
            fontSize={12}
            zIndex={10}
            rotate={-20}
            style={{ display: 'block', margin: scale > 1.0 ? '0' : 'auto' }}
            fontColor="rgba(0, 0, 0, 0.15)"
        >
          <Box sx={{ display: "flex",  justifyContent: scale > 1.0 ? "flex-start" : "center", alignItems: "center", bgcolor: "#fff", boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)", borderRadius: "8px", overflow: "hidden", transition: "all 0.15s ease-out", margin: scale > 1.0 ? "0" : "auto",}}>
            <Page
              key={`page_${pageNumber}`}
              pageNumber={pageNumber}
              width={calculatedWidth}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Box>
          </WaterMark>
            </Box>
        </Document>
        )}
          </Box>
        </Grid>

        {/* Notes Sidebar: rendered ONLY if role is student */}
        {isStudent && (
          <Grid item xs={12} md={4} lg={3.5} sx={{ display: "flex", flexDirection: "column" }}>
            <DocumentNote documentId={propDocumentId} pageNumber={pageNumber} />
      </Grid>
        )}
        </Grid>

      {/* Page Navigation and Zoom Controls */}
       <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, marginTop: 2, justifyContent: "center"  }}>
          <IconButton
            onClick={goToPreviousPage}
            disabled={pageNumber <= 1}
             sx={{
                width: 36,
                height: 36,
              border: "1px solid #D8B4FE",
              color: "#9834F0",
              backgroundColor: "#FFFFFF",
              "&:hover": {
                backgroundColor: "#F3E8FF" },
                "&.Mui-disabled": { borderColor: "#E5E7EB", color: "#D1D5DB" },
            }}
          >
            <NavigateBeforeIcon fontSize="small" />
          </IconButton>
          <Typography
              sx={{
                fontWeight:500,
                fontSize: "14px",
                color: "#6B7280"
                }}
            >
            Page
          </Typography>
          <TextField
            value={inputPageNumber}
            onChange={handlePageNumberChange}
            onBlur={handlePageNumberSubmit}
            onKeyPress={handleKeyPress}
            size="small"
            inputProps={{ min: 1, max: numPages }}
            sx={{
                width: "48px",
                "& .MuiOutlinedInput-root": {
                  height: "36px",
                  borderRadius: "6px",
                  "& fieldset": { borderColor: "#E5E7EB" },
                  "&:hover fieldset": { borderColor: "#9834F0" },
                  "&.Mui-focused fieldset": { borderColor: "#9834F0" },
                },
                "& .MuiInputBase-input": {
                  textAlign: "center",
                  fontWeight: 600,
                  fontSize: "14px",
                  padding: "4px",
              },
            }}
          />
          <Typography sx={{fontWeight:'500', fontSize: "14px", color: "#6B7280" }}>
            of {numPages || "..."}
          </Typography>
          <IconButton
            onClick={goToNextPage}
            disabled={pageNumber >= numPages}
            sx={{
              width: 36,
              height: 36,
              border: "1px solid #D8B4FE",
              color: "#9834F0",
              backgroundColor: "#FFFFFF",
              "&:hover": {
                backgroundColor: "#F3E8FF" },
                "&.Mui-disabled": { borderColor: "#E5E7EB", color: "#D1D5DB" },
            }}
          >
            <NavigateNextIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={handleZoomOut}
            disabled={scale <= 0.5}
            sx={{
              width: 36,
              height: 36,
              ml: 1,
              border: "1px solid #D8B4FE",
              color: "#9834F0",
                backgroundColor: "#FFFFFF",
                "&:hover": { backgroundColor: "#F3E8FF" },
                "&.Mui-disabled": { borderColor: "#E5E7EB", color: "#D1D5DB" },
            }}
            >
              <ZoomOutIcon fontSize="small" />
            </IconButton>

            <IconButton
            onClick={handleZoomIn}
            disabled={scale >= 2.0}
              sx={{
                width: 36,
                height: 36,
                border: "1px solid #D8B4FE",
                color: "#9834F0",
                backgroundColor: "#FFFFFF",
                "&:hover": { backgroundColor: "#F3E8FF" },
                "&.Mui-disabled": { borderColor: "#E5E7EB", color: "#D1D5DB" },
              }}
          >
            <ZoomInIcon fontSize="small" />
          </IconButton>
          </Box>
    </Box>
  );
};

export default PdfComponent;
